import {
  getFontCss,
  styleConstants,
  Tag,
  TagScales,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import Box from 'src/components/Box/Box'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import NoPeople from 'src/components/NoPeople/NoPeople'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import ObjectTableHeaderRow from 'src/components/ObjectTableHeaderRow/ObjectTableHeaderRow'
import SortableTableHeader, {
  SortableTableHeaderAlignment,
} from 'src/components/SortableTableHeader/SortableTableHeader'
import SvgOrganizationFilled from 'src/components/Svgs/SvgOrganizationFilled'
import TestId from 'src/components/TestId/TestId'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import Trns from 'src/localization/Trns'
import { UserFeature, UserListModel } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  accountsAccountsUrl,
  budgetsAllBudgetsUrl,
  peopleMembersDetailUrl,
  virtualCardsAllCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import styled, { css } from 'styled-components'

const StyledSpanLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
  ${getFontCss(
    14,
    themeConstants.colors.cta,
    styleConstants.fontFamilies.regular,
  )};
  &:hover {
    ${getFontCss(
      14,
      themeConstants.colors.cta,
      styleConstants.fontFamilies.medium,
    )};
  }
`

const CenteredObjectTableBodyCell = styled(ObjectTableBodyCell)`
  vertical-align: middle;
`

interface StatsDisplayProps {
  value: number | undefined
  testId: string
  pathname: string
  search: string
}

const StatDisplay: React.FC<StatsDisplayProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useDispatch()

  return props.value && props.value > 0 ? (
    <TestId testId={props.testId}>
      <StyledSpanLink
        onKeyDown={undefined}
        role={'button'}
        tabIndex={0}
        onClick={(e): void => {
          e.preventDefault()
          dispatch(
            connectedPush({
              pathname: props.pathname,
              search: props.search,
              state: {},
              hash: '',
            }),
          )
        }}
      >
        {props.value.toLocaleString(locale)}
      </StyledSpanLink>
    </TestId>
  ) : (
    <span
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {'0'}
    </span>
  )
}

const NullValue: React.FC = () => {
  return (
    <span
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      {'--'}
    </span>
  )
}

interface PeopleMembersListProps {
  people: UserListModel[]
  loading?: boolean
  sortableProps: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
}

enum sortValues {
  name = 'firstName',
  organizationRole = 'organizationRole',
  joinedAt = 'createdAt', // need backend to create a joinedAt sort field
  lastLoggedInAt = 'lastLoggedInAt',
}

const PeopleMembersList: React.FC<PeopleMembersListProps> = (props) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()
  const currentUser = useSelector(selectUserListModelFromState)
  const {
    ORGANIZATION_USER_STATISTICS: userFeaturesFromState,
    VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures,
  } =
    useQueryUserFeatures([
      UserFeature.ORGANIZATION_USER_STATISTICS,
      UserFeature.VIEW_VIRTUAL_CARD_FEATURES,
    ]).data || {}

  const user = useSelector(selectUserListModelFromState)

  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })

  const departmentsEnabled = orgFeatureFlags?.departmentsEnabled === true

  const canViewFullPersonStats =
    userFeaturesFromState || currentUser?.hasOrgAdminPermissions
  // locale
  useEffect(() => {
    if (locale !== undefined) {
      moment.locale(locale)
    }
  }, [locale])

  return (
    <Box orgBookkeeper noShadow noPadding>
      <ObjectTable>
        {props.people.length > 0 && (
          <ObjectTableHeaderRow>
            <SortableTableHeader
              css={css`
                ${canViewFullPersonStats ? 'width: 34%;' : 'width: 85%;'}
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
              label={<Trns source={'Name'} />}
              onClick={
                props.sortableProps
                  ? (): void => {
                      props.sortableProps?.setSort(sortValues.name)
                      analytics?.track(
                        'people org member list - sort people - table header press',
                        {
                          screen: '/people/members',
                          interaction: 'table header press',
                          intent: 'sort people',
                          sortField: sortValues.name,
                          sortDirection: props.sortableProps?.sortDescending
                            ? 'DESC'
                            : 'ASC',
                        },
                      )
                    }
                  : undefined
              }
              isSorted={props.sortableProps?.sortBy === sortValues.name}
              sortDescending={props.sortableProps?.sortDescending}
            />
            {canViewFullPersonStats && (
              <SortableTableHeader
                css={css`
                  width: 12%;
                `}
                alignment={SortableTableHeaderAlignment.LeftAligned}
                label={<Trns source={'Status'} />}
              />
            )}
            <SortableTableHeader
              css={css`
                ${canViewFullPersonStats ? 'width: 12%;' : 'width: 24%;'}
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
              label={<Trns source={'Role'} />}
              onClick={
                props.sortableProps
                  ? (): void => {
                      props.sortableProps?.setSort(sortValues.organizationRole)
                      analytics?.track(
                        'people org member list - sort people - table header press',
                        {
                          screen: '/people/members',
                          interaction: 'table header press',
                          intent: 'sort people',
                          sortField: sortValues.organizationRole,
                          sortDirection: props.sortableProps?.sortDescending
                            ? 'DESC'
                            : 'ASC',
                        },
                      )
                    }
                  : undefined
              }
              isSorted={
                props.sortableProps?.sortBy === sortValues.organizationRole
              }
              sortDescending={props.sortableProps?.sortDescending}
            />
            {departmentsEnabled && canViewFullPersonStats && (
              <SortableTableHeader
                css={css`
                  width: 12%;
                `}
                alignment={SortableTableHeaderAlignment.LeftAligned}
                label={<Trns source={'Team'} />}
              />
            )}
            {canViewFullPersonStats && (
              <>
                <SortableTableHeader
                  css={css`
                    width: 12%;
                  `}
                  alignment={SortableTableHeaderAlignment.LeftAligned}
                  label={<Trns source={'Accounts'} />}
                />
                {hasVirtualCardFeatures && (
                  <SortableTableHeader
                    css={css`
                      width: 12%;
                    `}
                    alignment={SortableTableHeaderAlignment.LeftAligned}
                    label={<Trns source={'Budgets'} />}
                  />
                )}
                {hasVirtualCardFeatures && (
                  <SortableTableHeader
                    css={css`
                      width: 12%;
                    `}
                    alignment={SortableTableHeaderAlignment.LeftAligned}
                    label={<Trns source={'Virtual Cards'} />}
                  />
                )}
              </>
            )}
            <SortableTableHeader
              css={css`
                width: 6%;
              `}
              alignment={SortableTableHeaderAlignment.RightAligned}
            />
          </ObjectTableHeaderRow>
        )}
        {props.people.length > 0 &&
          props.people.map((person, i) => {
            const {
              firstName,
              lastName,
              email,
              id,
              orgRoleDisplayTagTitle,
              hasOrgOwnerPermissions,
              department,
            } = person

            return (
              <ObjectTableBodyRow
                key={i}
                to={peopleMembersDetailUrl(id)}
                ariaLabelSource={'People Details'}
                testId={`people-row-${id}`}
                css={css`
                  ${person.deactivated &&
                  css`
                    background-color: ${styleConstants.colors
                      .gray_light}80; // 80 added for 50% transparency
                  `}
                `}
              >
                <CenteredObjectTableBodyCell>
                  <>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                      `}
                      key={i}
                      onClick={(): void => {
                        analytics?.track(
                          'people org member list - open user detail - list item press',
                          {
                            screen: '/people/members',
                            interaction: 'list item press',
                            intent: 'open user detail',
                            viewedUserId: id,
                          },
                        )
                      }}
                      onKeyPress={(): null => {
                        return null
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <UserAvatar
                        css={css`
                          margin-right: 10px;
                        `}
                        user={person}
                        size={AvatarSizes.Small}
                      />
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                        `}
                      >
                        <span>
                          {firstName && lastName && (
                            <span
                              css={css`
                                ${getFontCss(
                                  16,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.medium,
                                )};
                              `}
                            >
                              {`${firstName} ${lastName} `}
                            </span>
                          )}
                          {id === currentUser?.id && (
                            <span
                              css={css`
                                ${getFontCss(
                                  16,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              <Trns source={'(You)'} />
                            </span>
                          )}
                        </span>

                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              styleConstants.colors.gray_darkest,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {email}
                        </span>
                      </div>
                    </div>
                  </>
                </CenteredObjectTableBodyCell>
                {!person.deactivated ? (
                  <>
                    {canViewFullPersonStats && (
                      <CenteredObjectTableBodyCell
                        css={css`
                          ${getFontCss(
                            12,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        <Trns source={'Active'} />
                      </CenteredObjectTableBodyCell>
                    )}
                    <CenteredObjectTableBodyCell>
                      <Tag
                        text={<Trns source={orgRoleDisplayTagTitle} />}
                        icon={
                          hasOrgOwnerPermissions ? (
                            <SvgOrganizationFilled style={TagScales.medium} />
                          ) : undefined
                        }
                      />
                    </CenteredObjectTableBodyCell>
                    {departmentsEnabled && canViewFullPersonStats && (
                      <CenteredObjectTableBodyCell
                        css={css`
                          ${getFontCss(
                            12,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        {`${department?.name || ''}`}
                      </CenteredObjectTableBodyCell>
                    )}
                    {canViewFullPersonStats && (
                      <>
                        <CenteredObjectTableBodyCell>
                          <StatDisplay
                            value={person.accounts}
                            testId={person.id + '-Accounts'}
                            pathname={accountsAccountsUrl()}
                            search={`?user=${id}`}
                          />
                        </CenteredObjectTableBodyCell>
                        {hasVirtualCardFeatures && (
                          <CenteredObjectTableBodyCell>
                            <StatDisplay
                              value={person.budgets}
                              testId={person.id + '-Budgets'}
                              pathname={budgetsAllBudgetsUrl()}
                              search={`?statuses=ACTIVE${id !== undefined ? `&users=${id}` : ''}`}
                            />
                          </CenteredObjectTableBodyCell>
                        )}
                        {hasVirtualCardFeatures && (
                          <CenteredObjectTableBodyCell>
                            <StatDisplay
                              value={person.virtualCards}
                              testId={person.id + '-VirtualCards'}
                              pathname={virtualCardsAllCardsUrl()}
                              search={`?statuses=ACTIVE${id !== undefined ? `&recipients=${id}` : ''}`}
                            />
                          </CenteredObjectTableBodyCell>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {canViewFullPersonStats && (
                      <CenteredObjectTableBodyCell
                        css={css`
                          ${getFontCss(
                            12,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.regular,
                          )};
                        `}
                      >
                        <Trns source={'Deactivated'} />
                      </CenteredObjectTableBodyCell>
                    )}
                    <CenteredObjectTableBodyCell>
                      <NullValue />
                    </CenteredObjectTableBodyCell>
                    {departmentsEnabled && (
                      <CenteredObjectTableBodyCell>
                        <NullValue />
                      </CenteredObjectTableBodyCell>
                    )}
                    {canViewFullPersonStats && (
                      <>
                        <CenteredObjectTableBodyCell>
                          <NullValue />
                        </CenteredObjectTableBodyCell>
                        <CenteredObjectTableBodyCell>
                          <NullValue />
                        </CenteredObjectTableBodyCell>
                        <CenteredObjectTableBodyCell>
                          <NullValue />
                        </CenteredObjectTableBodyCell>
                      </>
                    )}
                  </>
                )}
                <CenteredObjectTableBodyCell>
                  <AnimatedTableChevron />
                </CenteredObjectTableBodyCell>
              </ObjectTableBodyRow>
            )
          })}
      </ObjectTable>
      {props.loading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            flex-grow: 2;
          `}
        >
          <LoadingBoxBottom />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          {props.people.length === 0 && (
            <NoPeople
              titleText={<Trns source={'No one matched your search.'} />}
              topSubtitleText={
                <div
                  css={css`
                    flex-direction: column;
                  `}
                >
                  <Trns
                    source={'Check the keyword you typed for any errors.'}
                  />
                </div>
              }
            />
          )}
        </div>
      )}
    </Box>
  )
}

export default PeopleMembersList
